import axios from 'axios';
import { getLlmApiKey } from '../utils/llmApiKey';
import { getAiProvider } from '../utils/aiProvider';
import { resolveApiBaseUrl } from './resolveApiBaseUrl';
import { attachAuth } from './authToken';

// Create a custom logger
const chatApiLogger = (message, data = null) => {
  const timestamp = new Date().toISOString();
  const logMessage = `[CHAT API ${timestamp}] ${message}`;

  if (data) {
    console.log(logMessage, data);
  } else {
    console.log(logMessage);
  }
};

// API base URL (always ends in `/api`). See resolveApiBaseUrl.js for precedence.
export const API_BASE_URL = resolveApiBaseUrl();

// Create axios instance with common configuration
const chatApiClient = axios.create({
  baseURL: `${API_BASE_URL}/chat`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach the bearer token + active-org header to every chat-API request (SAAS.3.1).
chatApiClient.interceptors.request.use(attachAuth);

// Request interceptor for logging
chatApiClient.interceptors.request.use(
  (config) => {
    // Special handling for context data to ensure we capture relationship info
    if (config.data && config.url.includes('/stream')) {
      try {
        const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;

        // Create a separate log for context details
        if (requestData.context && requestData.context.selected_items) {
          chatApiLogger('CONTEXT DATA - Selected Items:', JSON.stringify(requestData.context.selected_items, null, 2));

          // Log specific info about relationships within selected items
          requestData.context.selected_items.forEach((item, index) => {
            if (item.relationships) {
              chatApiLogger(`ITEM ${index + 1} RELATIONSHIPS:`, JSON.stringify(item.relationships, null, 2));
            }
          });
        }
      } catch (e) {
        chatApiLogger('Error parsing request data for detailed logging:', e);
      }
    }

    // AR9b: attach a user-supplied LLM API key (entered in the UI, persisted in
    // localStorage) so it overrides the backend-configured key for this request.
    const userKey = getLlmApiKey();
    if (userKey) config.headers['X-LLM-API-Key'] = userKey;

    // #106: attach the user-selected provider (source) so the backend serves
    // models / chat from GitHub Models or OpenRouter as chosen. Empty => default.
    const userProvider = getAiProvider();
    if (userProvider) config.headers['X-LLM-Provider'] = userProvider;

    // CONF3: never log request headers — they carry the user's X-LLM-API-Key.
    chatApiLogger(`Request: ${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
      data: config.data
    });
    return config;
  },
  (error) => {
    chatApiLogger(`Request Error:`, error);
    return Promise.reject(error);
  }
);

// Helper function to process error responses
const processErrorResponse = (error) => {
  const status = error.response?.status;
  const data = error.response?.data;

  // Create a standardized error object
  const errorObj = {
    message: 'Sorry, something went wrong. Please try again.',
    details: error.message || 'Unknown error',
    isRateLimit: false,
    statusCode: status || 500,
    retryAfter: null,
    fullError: null
  };

  // Check if the error has a response with our standardized error format
  if (data?.error) {
    const errorData = data.error;
    errorObj.message = errorData.message;
    errorObj.details = errorData.details;
    errorObj.isRateLimit = errorData.is_rate_limit || false;
    errorObj.statusCode = errorData.status_code || status;
    errorObj.fullError = errorData;
  } else if (data?.detail) {
    // Handle FastAPI's default error format (the GH#22 429 body also carries
    // `detail` alongside `error_code`/`retry_after`).
    errorObj.details = data.detail;
  }

  // GH#22b: surface the backend rate-limit response (HTTP 429 + `error_code` +
  // `retry_after`, set by `_rate_limit_response()` in main.py for GH#22),
  // regardless of which shape matched above, so the UI can show a clear
  // "retry in N s" message and pick the warning treatment over a generic error.
  const headerRetry = error.response?.headers?.['retry-after'];
  const retryAfter = data?.retry_after ??
    (headerRetry != null ? parseInt(headerRetry, 10) : null);
  if (status === 429 || data?.error_code === 'RateLimitReached') {
    errorObj.isRateLimit = true;
    errorObj.statusCode = 429;
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      errorObj.retryAfter = retryAfter;
    }
    // Only override the message if the nested format didn't already supply one.
    if (!data?.error?.message) {
      errorObj.message = errorObj.retryAfter
        ? `Rate limit reached — please retry in ${errorObj.retryAfter} second${errorObj.retryAfter === 1 ? '' : 's'}.`
        : 'Rate limit reached — please wait a moment and try again, or switch AI model.';
    }
  }

  return errorObj;
};

// Response interceptor for logging
chatApiClient.interceptors.response.use(
  (response) => {
    chatApiLogger(`Response from ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      statusText: response.statusText,
      data: response.data && response.data.length > 500
        ? `${response.data.substring(0, 500)}... [truncated]`
        : response.data
    });
    return response;
  },
  (error) => {
    chatApiLogger(`Response Error from ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
      message: error.message,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      } : 'No response'
    });

    // Process the error into our standardized format
    const processedError = processErrorResponse(error);

    // Add the processed error to the original error object
    error.processedError = processedError;

    return Promise.reject(error);
  }
);

// #1040: a provider switch (Hub icon -> OpenRouter) can leave the send path
// reading a bare model id from localStorage that the NEW provider's live API
// will reject (OpenRouter requires 'vendor/model'; see
// AzureModelManager._validate_model_for_provider on the backend, which now
// rejects the same shape at construction time instead of only downstream).
// useAiModels clears/refreshes 'preferredAiModel' around a provider switch,
// but this is a last-line-of-defense check right where the request body is
// built: never forward an id that is already known-malformed for the active
// provider. Omitting `model` lets the backend pick a provider-correct
// default (ChatService._default_model_for) instead of a guaranteed-bad one.
const _isWellFormedModelForProvider = (model, provider) => {
  if (!model) return false;
  if (provider === 'openrouter') return model.includes('/');
  return true;
};

const _resolvePreferredModel = () => {
  const storedModel = localStorage.getItem('preferredAiModel');
  return _isWellFormedModelForProvider(storedModel, getAiProvider()) ? storedModel : null;
};

// #492: module-level dedup/cache for the model catalogue. Both useAiModels (chat panel)
// and useAiFunctions (per-item tools) fetch /models on mount, and on the item+chat layout
// they mount together — two identical requests for a per-session-static catalogue. Cache
// the resolved value and share any in-flight request; `force` refetches (used on provider
// switch, where the catalogue actually changes).
let _modelsCache = null;
let _modelsInFlight = null;

// Test helper: clear the module-level model cache so a fresh mock takes effect per test
// (mirrors useTypeMap.__resetTypeMapCache). #492.
export function __resetModelsCache() { _modelsCache = null; _modelsInFlight = null; }

// Chat API functions
export const chatApi = {
  /**
   * Send a message to the chat API
   * @param {string} message - The message to send
   * @param {object} context - Optional context data
   * @param {array} history - Optional chat history
   * @returns {Promise<object>} - Promise resolving to the API response
   */
  sendMessage: async (message, context = null, history = [], sessionId = 'default') => {
    try {
      // #1040: never forward an id that's already malformed for the active
      // provider (see _resolvePreferredModel above).
      const preferredModel = _resolvePreferredModel();

      const response = await chatApiClient.post('/message', {
        message,
        context,
        history: history.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        session_id: sessionId,
        model: preferredModel  // Include the selected model with the request
      });

      return response.data;
    } catch (error) {
      chatApiLogger('Error sending message:', error);
      throw error; // Let the component handle the processed error
    }
  },

  /**
   * Stream a message from the chat API using Server-Sent Events
   * @param {string} message - The message to send
   * @param {object} context - Optional context data
   * @param {array} history - Optional chat history
   * @param {function} onChunk - Callback for each chunk received
   * @param {string} sessionId - Session ID for maintaining conversation state
   * @returns {Promise<object>} - Promise resolving when stream completes
   */
  streamMessage: async (message, context = null, history = [], onChunk = null, sessionId = 'default') => {
    try {
      chatApiLogger('Starting streaming message', {
        message,
        context: context ? 'provided' : 'null',
        historyLength: history.length,
        sessionId
      });

      // #1040: never forward an id that's already malformed for the active
      // provider (see _resolvePreferredModel above).
      const preferredModel = _resolvePreferredModel();
      chatApiLogger('Using model from localStorage:', preferredModel);

      // API base URL is handled by the axios instance

      // Step 1: Start the streaming session with POST /stream
      const startResponse = await chatApiClient.post('/stream', {
        message,
        context,
        history: history.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        session_id: sessionId,
        model: preferredModel  // Include the selected model with the request
      });

      if (startResponse.data.status !== 'streaming_started') {
        throw new Error('Failed to start streaming session');
      }

      // RT1: the POST returns a one-time, unguessable stream_id (capability token).
      // The EventSource GET authenticates via this token, not the bearer header
      // (EventSource cannot set headers) — so an attacker can no longer hijack a
      // stream by guessing the session id.
      const streamId = startResponse.data.stream_id;
      if (!streamId) {
        throw new Error('Failed to start streaming session: no stream id returned');
      }

      // Step 2: Connect to the SSE endpoint
      return new Promise((resolve, reject) => {
        try {
          // Create EventSource for SSE
          const eventSourceUrl = `${API_BASE_URL}/chat/stream?stream_id=${encodeURIComponent(streamId)}`;
          chatApiLogger('Opening SSE connection to:', eventSourceUrl);

          const eventSource = new EventSource(eventSourceUrl);
          let fullContent = '';

          // Handle connection open
          eventSource.onopen = () => {
            chatApiLogger('EventSource connection opened');
          };

          // Handle incoming messages
          eventSource.onmessage = (event) => {
            try {
              // Log the raw event data for debugging
              chatApiLogger('Raw SSE data received:', event.data);

              // Handle empty or malformed data
              if (!event.data || event.data.trim() === '') {
                chatApiLogger('Empty SSE data received, skipping');
                return;
              }

              let data;
              try {
                // EventSource will automatically handle the 'data: ' prefix
                // But we might need to handle cases where there are nested data: prefixes
                let dataStr = event.data;

                // Check if the string still starts with "data:" (possible double prefixing)
                // This can happen with certain SSE implementations
                while (dataStr.trim().startsWith('data:')) {
                  dataStr = dataStr.trim().substring(5).trim();
                  chatApiLogger('Removed additional data: prefix from event data');
                }

                // Handle newlines in the data that might affect parsing
                dataStr = dataStr.replace(/\n\n$/, '');

                // Extra debugging
                chatApiLogger('Processed SSE data before parsing:', dataStr.substring(0, 100));

                data = JSON.parse(dataStr);
                chatApiLogger('Successfully parsed SSE data into JSON');
              } catch (parseError) {
                chatApiLogger('Failed to parse SSE data, skipping this chunk:', parseError);
                chatApiLogger('Raw data that failed to parse:', event.data);
                return; // Skip this malformed chunk
              }

              // Handle new standardized error format. The standardized payload carries
              // `error` as a structured OBJECT ({message, details, status_code, ...}).
              // Old-style payloads (below) instead set `error` to a STRING message, so
              // gate this branch on the object shape — otherwise an old-style error
              // falls in here and every `data.error.<field>` reads `undefined`, mangling
              // the message and losing rate-limit detection (#455 bug 3).
              if (data.error && typeof data.error === 'object') {
                chatApiLogger('Error in stream (standardized format):', data.error);
                eventSource.close();

                const errorObj = {
                  message: data.error.message || 'An error occurred during streaming',
                  details: data.error.details || data.error.message || 'Unknown error',
                  isRateLimit: data.error.is_rate_limit || data.error.error_code === 'RateLimitReached' || false,
                  statusCode: data.error.status_code || 500,
                  retryAfter: data.error.retry_after ?? null,
                  fullError: data.error
                };

                // Reject with the processed error
                reject({ processedError: errorObj });
                return;
              }

              // Old-style error: `type: 'error'` with a STRING `error` message (the shape
              // emitted by the backend StreamingManager / LangChainAdapter). Now reachable
              // because the standardized branch above requires an object, so the real
              // message surfaces in `details` instead of the constant 'Unknown error'.
              if (data.type === 'error') {
                chatApiLogger('Error in stream (old format):', data);
                eventSource.close();

                const legacyMessage = typeof data.error === 'string' ? data.error : null;
                // #1059: the backend now classifies known provider failures and sends a
                // sentence written for the user in `user_message`. Prefer it over the
                // constant below, which is all the chat window used to show — the actual
                // cause only ever reached `details`, behind the info icon.
                const errorObj = {
                  message: data.user_message || 'Sorry, something went wrong. Please try again.',
                  details: legacyMessage || 'Unknown error',
                  isRateLimit: data.rate_limited || data.error_details?.code === 429 || false,
                  isProviderUnavailable: data.provider_unavailable || false,
                  failureKind: data.failure_kind || null,
                  // The SHORT reason ("AI service unreachable"), distinct from the
                  // sentence above. The persistent notice names the reason with this
                  // instead of restating `message`, which would stack the same
                  // paragraph twice in one column.
                  failureTitle: data.failure_title || null,
                  statusCode: data.error_details?.code || 500,
                  retryAfter: data.retry_after ?? null,
                  fullError: data
                };

                reject({ processedError: errorObj });
                return;
              }

              // Handle heartbeat messages (don't add to content, just update UI and reset timeout)
              if (data.type === 'heartbeat') {
                chatApiLogger('Received heartbeat:', data.chunk || data.content);

                // Reset any connection timeout timers
                if (window._heartbeatTimeout) {
                  clearTimeout(window._heartbeatTimeout);
                }

                // Set new timeout to detect if heartbeats stop coming
                window._heartbeatTimeout = setTimeout(() => {
                  chatApiLogger('No heartbeat received in 30 seconds, this might indicate a connection issue');
                }, 30000);

                // Update last activity time to prevent auto-disconnect
                window._lastHeartbeatTime = Date.now();

                // Call the onChunk callback with the full data object for consistency
                if (onChunk) {
                  onChunk(data, fullContent);
                }
                return; // Don't process heartbeat as regular content
              }

              // Handle thinking/intermediate steps
              if (data.type === 'thinking') {
                chatApiLogger('Agent thinking:', data.chunk || data.content);
                if (onChunk) {
                  onChunk(data, fullContent, {
                    isComplete: false,
                    type: 'thinking',
                    thinking_type: data.thinking_type
                  });
                }
                return; // Don't add thinking steps to final content
              }

              // Handle regular content - try different possible formats
              // Check content field (primary)
              if (data.content !== undefined) {
                // Handle string content
                if (typeof data.content === 'string') {
                  fullContent += data.content;
                  chatApiLogger('Added content to fullContent, new length:', fullContent.length);
                } else {
                  // If content is not a string, use it as is
                  fullContent = JSON.stringify(data.content);
                  chatApiLogger('Non-string content received, stringified:', fullContent.substring(0, 50));
                }

                if (onChunk) {
                  chatApiLogger('Calling onChunk with content from data.content field');
                  onChunk(data, fullContent);
                }
              }
              // Also check for chunk field (alternate format)
              else if (data.chunk !== undefined) {
                // Handle string chunk
                if (typeof data.chunk === 'string') {
                  fullContent += data.chunk;
                  chatApiLogger('Added chunk to fullContent, new length:', fullContent.length);
                } else {
                  // If chunk is not a string, stringify and use it
                  const chunkStr = JSON.stringify(data.chunk);
                  fullContent += chunkStr;
                  chatApiLogger('Non-string chunk received, stringified:', chunkStr.substring(0, 50));
                }

                if (onChunk) {
                  // Normalize to expected format
                  const normalizedData = {
                    ...data,
                    content: data.chunk
                  };
                  chatApiLogger('Calling onChunk with normalized data from chunk field');
                  onChunk(normalizedData, fullContent);
                }
              } else {
                chatApiLogger('Received data with no content or chunk field:', data);

                // If no content or chunk, but there's some other data, pass it to the callback anyway
                if (onChunk && Object.keys(data).length > 0) {
                  chatApiLogger('Calling onChunk with raw data object');
                  onChunk(data, fullContent);
                }
              }

              // Check if it's the end of the stream
              if (data.type === 'end' || data.done) {
                chatApiLogger('Stream completed, closing EventSource');

                // Use the accumulated fullContent as the final response
                // Don't extract from context.chat_history as it may contain stale data
                const finalContent = fullContent;
                chatApiLogger('Using accumulated fullContent as final response, length:', finalContent.length);

                eventSource.close();
                resolve({ message: finalContent });
              }
            } catch (err) {
              chatApiLogger('Error processing stream chunk:', err);
              // Don't close the stream for parsing errors of individual chunks
            }
          };

          // Handle connection errors with retry logic
          let retryCount = 0;
          const maxRetries = 3;
          // We'll use the session ID if we implement reconnection in the future

          eventSource.onerror = (error) => {
            chatApiLogger('EventSource error:', error);

            // Check if we should retry
            if (retryCount < maxRetries) {
              retryCount++;
              const retryDelay = 1000; // 1 second
              chatApiLogger(`Connection error. Retry attempt ${retryCount}/${maxRetries} in 1 second...`);

              // If connection is closed, try to reconnect with same session ID
              if (eventSource.readyState === 2) { // CLOSED
                setTimeout(() => {
                  chatApiLogger('Attempting to reconnect...');
                  // Try to reconnect to the same session - start from scratch
                  chatApiLogger('Connection lost. Please try sending your message again.');
                }, retryDelay);
              }
              return;
            }

            // After max retries, close and reject
            eventSource.close();

            const errorObj = {
              message: 'Connection error during streaming.',
              details: `The connection to the server was lost after ${maxRetries} retry attempts. Please try again.`,
              isRateLimit: false,
              statusCode: 0, // Connection error
              fullError: error
            };

            reject({ processedError: errorObj });
          };

          // Set a safety timeout (10 minutes to match backend)
          setTimeout(() => {
            if (eventSource.readyState !== 2) { // 2 = CLOSED
              chatApiLogger('Stream timeout - closing connection');
              eventSource.close();
              resolve({ message: fullContent });
            }
          }, 600000); // 10 minutes in milliseconds

        } catch (err) {
          chatApiLogger('Error setting up EventSource:', err);

          const errorObj = {
            message: 'Failed to setup streaming connection.',
            details: err.message || 'Unknown error setting up connection',
            isRateLimit: false,
            statusCode: 500,
            fullError: err
          };

          reject({ processedError: errorObj });
        }
      });
    } catch (error) {
      chatApiLogger('Error in streamMessage:', error);
      throw error; // Let the component handle the processed error
    }
  },

  /**
   * Update context for the chat session
   * @param {object} contextData - The context data to update
   * @returns {Promise<object>} - Promise resolving to the API response
   */
  updateContext: async (contextData) => {
    try {
      const response = await chatApiClient.post('/context', contextData);
      return response.data;
    } catch (error) {
      chatApiLogger('Error updating context:', error);
      throw error;
    }
  },

  /**
   * Clear chat history
   * @returns {Promise<object>} - Promise resolving to the API response
   */
  clearHistory: async () => {
    try {
      const response = await chatApiClient.post('/history/clear', {});
      return response.data;
    } catch (error) {
      chatApiLogger('Error clearing history:', error);
      throw error;
    }
  },

  /**
   * Get chat history
   * @param {string} sessionId - Optional session ID
   * @returns {Promise<object>} - Promise resolving to the API response
   */
  getHistory: async (sessionId = 'default') => {
    try {
      const response = await chatApiClient.get(`/history?session_id=${sessionId}`);
      return response.data;
    } catch (error) {
      chatApiLogger('Error getting history:', error);
      throw error;
    }
  },

  /**
   * Get available AI models
   * @returns {Promise<object>} - Promise resolving to the list of available models
   */
  getAvailableModels: ({ force = false } = {}) => {
    if (!force && _modelsCache) return _modelsCache;           // #492: served from cache
    if (!force && _modelsInFlight) return _modelsInFlight;     // #492: share the in-flight request
    _modelsInFlight = (async () => {
      try {
        const response = await chatApiClient.get('/models');
        _modelsCache = response.data;
        return response.data;
      } catch (error) {
        chatApiLogger('Error getting available models:', error);
        throw error;
      } finally {
        _modelsInFlight = null;
      }
    })();
    return _modelsInFlight;
  },

  /**
   * Set the current AI model
   * @param {string} modelId - The ID of the model to use
   * @returns {Promise<object>} - Promise resolving to the API response
   */
  setCurrentModel: async (modelId) => {
    try {
      const response = await chatApiClient.post('/models/current', {
        model: modelId
      });

      // Store the selected model in localStorage for persistence
      try {
        localStorage.setItem('preferredAiModel', modelId);
      } catch (storageError) {
        chatApiLogger('Error saving model preference to localStorage:', storageError);
      }

      return response.data;
    } catch (error) {
      chatApiLogger('Error setting current model:', error);
      throw error;
    }
  },

  // Add a log function that can be used for debugging
  logMessage: (message, data) => {
    chatApiLogger(message, data);
  },

  // Helper function to format errors consistently for UI display
  formatErrorForDisplay: (error) => {
    // If error already has the processed format, use it
    if (error.processedError) {
      return error.processedError;
    }

    // Process raw error objects
    return processErrorResponse(error);
  }
};

export default chatApiClient;